import React, { useState } from 'react';
import { GoalIcon } from '../../../icons';
import { CapabilityDomain, AssessmentQuestion, ExtendedDomainAssessment, ExtendedCapabilityDecision } from './types';
import { EXTENDED_SKILLS_QUESTIONS } from './extendedSkillsQuestions';
import { CAPABILITY_DOMAIN_LABELS } from './types';

interface ExtendedSkillsWizardProps {
  domains: CapabilityDomain[]; // Alleen domeinen waar required=true
  onComplete: (decisions: Partial<ExtendedCapabilityDecision>[]) => void;
  onCancel: () => void;
}

/**
 * ExtendedSkillsWizard
 * 
 * Diepgaande vragenlijst per domein (15-20 vragen):
 * - Huidige Situatie
 * - Concrete Vaardigheden
 * - Knelpunten
 * - Actieplan
 */
const ExtendedSkillsWizard: React.FC<ExtendedSkillsWizardProps> = ({
  domains,
  onComplete,
  onCancel
}) => {
  const [currentDomainIndex, setCurrentDomainIndex] = useState(0);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<CapabilityDomain, Record<string, any>>>({} as any);

  const currentDomain = domains[currentDomainIndex];
  const domainData = EXTENDED_SKILLS_QUESTIONS[currentDomain];
  const currentSection = domainData.sections[currentSectionIndex];
  const totalSections = domainData.sections.length;

  const handleAnswer = (questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [currentDomain]: {
        ...(prev[currentDomain] || {}),
        [questionId]: value
      }
    }));
  };

  const handleNext = () => {
    // Check if all required questions are answered
    const requiredQuestions = currentSection.questions.filter(q => q.required !== false);
    const domainAnswers = answers[currentDomain] || {};
    
    const allAnswered = requiredQuestions.every(q => {
      const answer = domainAnswers[q.id];
      if (q.type === 'multiselect') {
        return Array.isArray(answer) && answer.length > 0;
      }
      return answer !== undefined && answer !== null && answer !== '';
    });

    if (!allAnswered) {
      alert('Vul alle verplichte velden in om door te gaan.');
      return;
    }

    // Move to next section or next domain
    if (currentSectionIndex < totalSections - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    } else if (currentDomainIndex < domains.length - 1) {
      setCurrentDomainIndex(currentDomainIndex + 1);
      setCurrentSectionIndex(0);
    } else {
      // All done!
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    } else if (currentDomainIndex > 0) {
      setCurrentDomainIndex(currentDomainIndex - 1);
      setCurrentSectionIndex(EXTENDED_SKILLS_QUESTIONS[domains[currentDomainIndex - 1]].sections.length - 1);
    }
  };

  const handleComplete = () => {
    // Transform answers into ExtendedCapabilityDecision format
    const decisions: Partial<ExtendedCapabilityDecision>[] = domains.map(domain => ({
      domain,
      extendedAnswers: answers[domain] || {}
    }));

    onComplete(decisions);
  };

  const renderQuestion = (question: AssessmentQuestion) => {
    const domainAnswers = answers[currentDomain] || {};
    const value = domainAnswers[question.id];

    switch (question.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            placeholder={question.placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            placeholder={question.placeholder}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />
        );

      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">-- Kies een optie --</option>
            {question.options?.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'multiselect':
        return (
          <div className="space-y-2">
            {question.options?.map((option, idx) => {
              const isSelected = Array.isArray(value) && value.includes(option);
              return (
                <label key={idx} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      const currentValues = Array.isArray(value) ? value : [];
                      if (e.target.checked) {
                        handleAnswer(question.id, [...currentValues, option]);
                      } else {
                        handleAnswer(question.id, currentValues.filter(v => v !== option));
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700">{option}</span>
                </label>
              );
            })}
          </div>
        );

      case 'scale':
        return (
          <div className="space-y-2">
            {question.scale?.map((label, idx) => (
              <label key={idx} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name={question.id}
                  value={idx + 1}
                  checked={value === (idx + 1)}
                  onChange={() => handleAnswer(question.id, idx + 1)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700">{label}</span>
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const progress = Math.round(
    ((currentDomainIndex * totalSections + currentSectionIndex + 1) / (domains.length * totalSections)) * 100
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90dvh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 text-white rounded-t-xl">
          <h2 className="text-xl font-bold">
            <GoalIcon size={20} className="inline" /> Uitgebreide Skills Assessment
          </h2>
          <p className="text-sm text-purple-100 mt-1">
            {CAPABILITY_DOMAIN_LABELS[currentDomain]} - {currentSection.title}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>
              Domein {currentDomainIndex + 1} van {domains.length}
            </span>
            <span>
              Sectie {currentSectionIndex + 1} van {totalSections}
            </span>
            <span className="font-semibold text-blue-600">
              {progress}% compleet
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Section Description */}
          {currentSection.description && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">{currentSection.description}</p>
            </div>
          )}

          {/* Questions */}
          {currentSection.questions.map((question, idx) => (
            <div key={question.id} className="space-y-2">
              <label className="block">
                <span className="text-sm font-medium text-gray-900">
                  {idx + 1}. {question.question}
                  {question.required !== false && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </span>
                <div className="mt-2">
                  {renderQuestion(question)}
                </div>
              </label>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Annuleren
          </button>

          <div className="flex gap-3">
            {(currentDomainIndex > 0 || currentSectionIndex > 0) && (
              <button
                onClick={handleBack}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                ← Vorige
              </button>
            )}

            <button
              onClick={handleNext}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700"
            >
              {currentDomainIndex === domains.length - 1 && currentSectionIndex === totalSections - 1
                ? 'Voltooien'
                : 'Volgende →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtendedSkillsWizard;
